/*
    Helper functions to determine state or view of the loan to display
*/
import { DateTime } from 'luxon';

import { PortalLoanSummaryFragment, PortalTransferDataFragment } from '@willow/graphql-iso/src/portal';
import { stripNullFromNullOrUndefined } from '@willow/shared-iso';
import { parseGqlIsoDate } from '@willow/types-iso';

export const isTransferredView = (loan: PortalLoanSummaryFragment): boolean => {
  if (!loan) return false;

  const { paymentDueStatus, loanStatus, transferData, payments, charges } = loan.currentSnapshot;

  const willTransferBeforeFirstPaymentDue = () => {
    // Display "transferred" if loan is scheduled to transfer before the first payment is due
    return (
      !!transferData &&
      parseGqlIsoDate(transferData.transferEffectiveDate) <= new Date(loan.configuration.firstDueDate) &&
      paymentDueStatus !== 'due'
    );
  };

  const isTransferringAndPaymentsAreFulfilled = () => {
    // Ensure loan is pending transfer OR transferred, and has transferData
    const isTransferredOrPendingTransfer = Boolean(
      (loanStatus === 'transferred' || loanStatus === 'pendingTransfer') && !!transferData,
    );
    // Ensure there will be no more payments due between now and the transfer date
    // (we do this by checking to see if no more months start before then)
    const inOrAfterLastMonthBeforeTransfer = getInOrAfterLastMonthBeforeTransfer(
      new Date(),
      stripNullFromNullOrUndefined(transferData),
    );
    // Ensure there are no outstanding payments due
    const noPaymentsDue = paymentDueStatus !== 'due';
    // Ensure there are no payments that are currently processing. If so, continue showing the payment screen.
    const noPaymentsProcessing = !payments.some((payment) => payment.status === 'PENDING');
    //Ensure there are no outstanding charges due
    const noFeesDue = !charges.some((charge) => charge.status === 'CHARGED');
    // Display "transferred" if the loan is transferred or nearing transfer and there are no more payments due or processing.

    return (
      isTransferredOrPendingTransfer &&
      inOrAfterLastMonthBeforeTransfer &&
      noPaymentsDue &&
      noPaymentsProcessing &&
      noFeesDue
    );
  };

  return willTransferBeforeFirstPaymentDue() || isTransferringAndPaymentsAreFulfilled();
};

export const getInOrAfterLastMonthBeforeTransfer = (
  todayDate: Date,
  transferData: PortalTransferDataFragment | undefined,
) => {
  if (!transferData) return false;
  const { legalTransferEffectiveDate } = transferData;
  if (!legalTransferEffectiveDate) return false;

  const today = DateTime.fromJSDate(todayDate);
  const lastDayBeforeTransfer = DateTime.fromJSDate(parseGqlIsoDate(legalTransferEffectiveDate)).minus({
    days: 1,
  });
  // Returns TRUE if today is after the transfer date
  // OR if there will be no more payments due between today and the transfer date
  // (we do this by checking to see if no more months start before then)
  return (
    today > lastDayBeforeTransfer ||
    (today.hasSame(lastDayBeforeTransfer, 'year') && today.hasSame(lastDayBeforeTransfer, 'month'))
  );
};

// Keeping this commented out for now. The `<PaidCurrentHeader />` is the only place
// outside of this file using `getInOrAfterLastMonthBeforeTransfer` and I think
// the logic could be as simple as this:
//
// export const isNextStatementBeforeTransfer = (
//   nextStatementDate: Date,
//   transferData: PortalTransferDataFragment | undefined | null,
// ) => {
//   if (!transferData || !transferData?.transferEffectiveDate) return true;
//   const lastDayBeforeTransfer = DateTime.fromJSDate(new Date(transferData.transferEffectiveDate)).minus({ days: 1 });
//   return DateTime.fromJSDate(nextStatementDate) < lastDayBeforeTransfer;
// };
